import React, {
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";
import AccountContext from "../utils/AccountsHelper";
import MwInputText from "../components/ui/MwInputText";
import MwSelector from "../components/ui/MwSelector";
import MwButton from "../components/ui/MwButton";
import MwModal from "../components/ui/MwModal";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import SelectorTree from "../components/ui/SelectAccount/SelectorTree";
import AccountCenterContext from "../utils/CostCenterAccHelper";
import CurrencyContext from "../utils/CurrencyHelper";
import { useTenant } from "../hooks";
import { MwSpinnerButton } from "../components/ui";
import { normalize } from "path-browserify";

const initialFormState = {
  id: "",
  parent_id: 0,
  accountNumber: "",
  accountType: "main",
  accountNameAR: "",
  accountNameEN: "",
  level: 0,
  accountCurrency: null,
  accountNature: "Cred",
  openingBalance: "",
  closingBalance: "",
  costCenterAccount: "",
  costCenterLabel: "",
  costCenterAccountType: "",
  affectType: "income",
};
const FormReducer = (state, action) => {
  switch (action.type) {
    case "SET_FIELD":
      return { ...state, [action.field]: action.value };
    case "RESET":
      return initialFormState;
    default:
      return state;
  }
};

const getNodeNameByLang = (node, isArabic) => {
  if (isArabic) return node?.name_ar || node?.name_en || "";
  return node?.name_en || node?.name_ar || "";
};

const normalizeAccountType = (value) => {
  const normalized = String(value || "").toLowerCase();
  if (normalized === "sub") return "secondary";
  if (normalized === "secondary") return "secondary";
  if (normalized === "main") return "primary";
  return "";
};

const resolveNodeType = (node) => {
  const explicitType = normalizeAccountType(node?.account_type);

  if (explicitType) return explicitType;

  return Array.isArray(node?.childs) && node.childs.length > 0
    ? "primary"
    : "secondary";
};
const flattenCenters = (nodes, isArabic, list = []) => {
  (Array.isArray(nodes) ? nodes : []).forEach((node, index) => {
    const type = resolveNodeType(node);

    list.push({
      id: String(node?.id || index),
      accountNumber: String(node?.account_no || ""),
      accountName: String(isArabic ? node?.name_ar || "" : node?.name_en || ""),
      accountType: type,
    });

    if (Array.isArray(node?.childs) && node.childs.length) {
      flattenCenters(node.childs, isArabic, list);
    }
  });

  return list;
};
export const AccountMngPop = ({
  isOpen = false,
  setIsOpen = () => {},
  onClose = () => {},
  actionData,
  code = "",
  loading = false,
  loadingChild = false,
  accounts,
}) => {
  const [t] = useTranslation("global");
  const isViewMode = actionData?.mode === "view";
  const [formData, dispatchForm] = useReducer(FormReducer, initialFormState);
  const {
    tenantId,
    companySelected,
    branchSelected,
    getSettingsByCompanySelected,
  } = useTenant();

  const [errors, setErrors] = useState({});

  const { currencies } = useContext(CurrencyContext);
  const [accountCurrency, setAccountCurrency] = useState([]);
  useEffect(() => {
    if (Array.isArray(currencies) && currencies.length) {
      const normalized = currencies.map((currency) => ({
        id: String(currency?.id || ""),
        value: String(currency?.currency_code || currency?.code || "")
          .trim()
          .toUpperCase(),
        label:
          String(
            i18next.dir(i18next.language) === "rtl"
              ? currency?.name_ar || ""
              : currency?.name_en || "",
          ).trim() ||
          String(currency?.currency_code || currency?.code || "")
            .trim()
            .toUpperCase(),
        lang: i18next.language,
      }));
      setAccountCurrency(normalized);
    }
  }, [currencies]);

  const mainCurrencyId = useMemo(() => {
    const normalizeCode = (value) =>
      String(value || "")
        .trim()
        .toUpperCase();

    const mainCurrencyCode = normalizeCode(
      getSettingsByCompanySelected?.mainCurrency ||
        getSettingsByCompanySelected?.currency,
    );
    if (!mainCurrencyCode) return null;
    return (
      (
        accountCurrency.find(
          (currency) => normalizeCode(currency.value) === mainCurrencyCode,
        ) || null
      )?.id || null
    );
  }, [getSettingsByCompanySelected, accountCurrency]);

  const { accounts: costCenters } = useContext(AccountCenterContext);
  const isArabic = i18next.dir(i18next.language) === "rtl";
  const flatCostCenters = useMemo(
    () => flattenCenters(costCenters, isArabic, []),
    [costCenters, isArabic],
  );
  const selectedCostCenterType = normalizeAccountType("secondary");
  const selectableCostCenters = useMemo(
    () =>
      flatCostCenters.filter(
        (center) => center.accountType === selectedCostCenterType,
      ),
    [flatCostCenters, selectedCostCenterType],
  );

  const handleInputChange = (field, value) => {
    dispatchForm({ type: "SET_FIELD", field, value });
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleSelectorChange = (field, value) => {
    dispatchForm({ type: "SET_FIELD", field, value });
  };
  const handleManualCostCenterNumberInput = (inputValue) => {
    const input = String(inputValue || "").trim();

    dispatchForm({
      type: "SET_FIELD",
      field: "costCenterAccount",
      value: input,
    });

    if (!input) {
      dispatchForm({
        type: "SET_FIELD",
        field: "costCenterLabel",
        value: "",
      });

      setErrors((prev) => ({
        ...prev,
        costCenterAccount: "",
      }));

      return;
    }

    const matches = flatCostCenters.filter((center) => {
      const acc = String(center.accountNumber || "").trim();
      return acc.startsWith(input);
    });

    if (matches.length === 0) {
      setErrors((prev) => ({
        ...prev,
        costCenterAccount: t("common.mustBeSubAccount"),
      }));

      dispatchForm({
        type: "SET_FIELD",
        field: "costCenterLabel",
        value: "",
      });

      return;
    }

    const secondaryAccount = matches.find(
      (center) => center.accountType === "secondary",
    );

    if (!secondaryAccount) {
      setErrors((prev) => ({
        ...prev,
        costCenterAccount: t("common.mustBeSubAccount"),
      }));

      dispatchForm({
        type: "SET_FIELD",
        field: "costCenterLabel",
        value: "",
      });

      return;
    }

    setErrors((prev) => ({
      ...prev,
      costCenterAccount: "",
    }));

    dispatchForm({
      type: "SET_FIELD",
      field: "costCenterAccount",
      value: secondaryAccount.accountNumber,
    });

    dispatchForm({
      type: "SET_FIELD",
      field: "costCenterLabel",
      value: secondaryAccount.accountName,
    });
  };
  const validateForm = () => {
    const newErrors = {};
    if (!formData.accountNumber?.toString().trim())
      newErrors.accountNumber = `${t("accounts.accountNumber")} ${t("common.required")}`;
    if (!formData.accountNameAR.trim())
      newErrors.accountNameAR = `${t("accounts.accountNameAr")} ${t("common.required")}`;
    if (!formData.accountNameEN.trim())
      newErrors.accountNameEN = `${t("accounts.accountNameEn")} ${t("common.required")}`;
    if (formData.costCenterAccount) {
      const selectedCenter = selectableCostCenters.find(
        (c) => String(c.accountNumber) === String(formData.costCenterAccount),
      );

      if (!selectedCenter) {
        newErrors.costCenterAccount = t("common.mustBeSubAccount");
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const findAccount = (nodes, id) => {
    if (!Array.isArray(nodes)) return null;

    for (const node of nodes) {
      if (node.id === id) return node;
      if (Array.isArray(node.childs) && node.childs.length) {
        const found = findAccount(node.childs, id);
        if (found) return found;
      }
    }
    return null;
  };
  const parentAccount = findAccount(accounts, actionData.parentId);
  const parentAccountDisplay = parentAccount
    ? `${getNodeNameByLang(parentAccount, isArabic)} - ${String(parentAccount?.account_no || "").trim()}`
    : "";
  const level = parentAccount ? parentAccount.level + 1 : 1;
  const modalTitle =
    actionData?.mode === "add"
      ? t("accounts.addAccount")
      : actionData?.mode === "edit"
        ? t("accounts.editAccount")
        : actionData?.mode === "view"
          ? t("dropdown.preview")
          : "";
  const showParentAccountInTitle =
    actionData?.mode === "add" &&
    Boolean(actionData?.parentId) &&
    Boolean(parentAccountDisplay);
  const handleSave = async () => {
    if (isViewMode) return;
    if (!validateForm()) return;

    const payload = {
      parent_id:
        actionData.mode === "edit"
          ? actionData.account.parent_id
          : actionData.parentId || 0,

      account_no: formData.accountNumber,
      account_type: formData.accountType === "main" ? "main" : "key",
      name_ar: formData.accountNameAR,
      name_en: formData.accountNameEN,
      level: actionData.mode === "edit" ? actionData.account.level : level,
      currency_accout_id: formData.accountCurrency,
      nat_account: formData.accountNature === "Debit" ? "Debit" : "Cred",
      opening_balance: formData.openingBalance,
      closed_balance: formData.closingBalance,
      cost_center_account: formData.costCenterAccount,
      effect_account: formData.affectType === "budget" ? "budget" : "income",
      tenant_id: tenantId,
      company_id: companySelected?.value,
      branch_id: branchSelected?.value,
    };

    if (actionData.mode === "add") {
      await addAccount(payload);
    } else if (actionData.mode === "edit") {
      await editAccount(payload, formData?.id);
    }

    dispatchForm({ type: "RESET" });

    setIsOpen(false);
  };
  const handleClose = () => {
    setIsOpen(false);
    dispatchForm({ type: "RESET" });
    errors && setErrors({});
    if (onClose) onClose();
  };

  // Guard against undefined translations
  useEffect(() => {
    if (
      (actionData?.mode === "edit" || actionData?.mode === "view") &&
      actionData.account
    ) {
      const account = actionData.account;
      console.log(account);

      dispatchForm({
        type: "SET_FIELD",
        field: "id",
        value: account.id || "",
      });
      dispatchForm({
        type: "SET_FIELD",
        field: "accountNumber",
        value: account.account_no || "",
      });
      dispatchForm({
        type: "SET_FIELD",
        field: "accountNameAR",
        value: account.name_ar || "",
      });
      dispatchForm({
        type: "SET_FIELD",
        field: "accountNameEN",
        value: account.name_en || "",
      });
      dispatchForm({
        type: "SET_FIELD",
        field: "accountCurrency",
        value: String(account.currency_accout_id || ""),
      });
      dispatchForm({
        type: "SET_FIELD",
        field: "accountType",
        value: account.account_type || "main",
      });
      dispatchForm({
        type: "SET_FIELD",
        field: "accountNature",
        value: account.nat_account || "debit",
      });
      dispatchForm({
        type: "SET_FIELD",
        field: "openingBalance",
        value: account.opening_balance || 0,
      });
      dispatchForm({
        type: "SET_FIELD",
        field: "closingBalance",
        value: account.closed_balance || 0,
      });
      dispatchForm({
        type: "SET_FIELD",
        field: "costCenterAccount",
        value: account.cost_center_account || "",
      });
      dispatchForm({
        type: "SET_FIELD",
        field: "costCenterLabel",
        value:
          flatCostCenters.find(
            (center) =>
              String(center.accountNumber) ===
              String(account.cost_center_account),
          )?.accountName || "",
      });
      const selectedCostCenterNumber = String(
        account.costCenterAccount?.value || account.costCenterAccount || "",
      ).trim();
      const selectedCenter = flatCostCenters.find(
        (center) => String(center.accountNumber) === selectedCostCenterNumber,
      );
      dispatchForm({
        type: "SET_FIELD",
        field: "costCenterAccountType",
        value:
          normalizeAccountType(
            account.costCenterAccountType || selectedCenter?.account_type,
          ) || "main",
      });
      dispatchForm({
        type: "SET_FIELD",
        field: "affectType",
        value: account.effect_account || "balance",
      });
    }
    if (actionData?.mode === "add") {
      dispatchForm({
        type: "RESET",
      });

      dispatchForm({
        type: "SET_FIELD",
        field: "accountType",
        value: actionData?.parentId ? "key" : "main",
      });
      if (code) {
        dispatchForm({
          type: "SET_FIELD",
          field: "accountNumber",
          value: code,
        });
      }
    }
  }, [actionData, flatCostCenters, code, mainCurrencyId]);

  useEffect(() => {
    if (actionData?.mode !== "add") return;
    if (formData.accountCurrency) return;
    const defaultCurrencyId =
      mainCurrencyId || accountCurrency?.[0]?.id || null;
    if (!defaultCurrencyId) return;
    dispatchForm({
      type: "SET_FIELD",
      field: "accountCurrency",
      value: defaultCurrencyId,
    });
  }, [
    actionData?.mode,
    formData.accountCurrency,
    mainCurrencyId,
    accountCurrency,
  ]);

  useEffect(() => {
    const selectedCostCenterNumber = String(
      formData.costCenterAccount?.value || formData.costCenterAccount || "",
    ).trim();
    if (!selectedCostCenterNumber) return;

    const selectedCenter = flatCostCenters.find(
      (center) => String(center.accountNumber) === selectedCostCenterNumber,
    );
    if (!selectedCenter) return;

    if (selectedCenter.accountType !== selectedCostCenterType) {
      handleSelectorChange("costCenterAccount", "");
      handleSelectorChange("costCenterLabel", "");
    }
  }, [flatCostCenters, formData.costCenterAccount, selectedCostCenterType]);

  const { addAccount, editAccount, loadingSave } = useContext(AccountContext);
  if (loading || loadingChild || loadingSave) {
    return (
      <MwModal onShow={isOpen} setOnShow={setIsOpen}>
        <div className="w-full  h-[20vh] flex justify-center items-center">
          <MwSpinnerButton />
        </div>
      </MwModal>
    );
  }
  return (
    <MwModal onShow={isOpen} setOnShow={setIsOpen}>
      <div className="flex flex-col h-full w-full bg-white rounded-lg shadow-lg">
        {/* Header */}

        <div className="p-4 border-b bg-slate-100">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-bold text-[#261759] text-lg">{modalTitle}</h2>
            {showParentAccountInTitle ? (
              <span className="text-sm font-medium text-slate-600">
                ({parentAccountDisplay})
              </span>
            ) : null}
          </div>
        </div>
        {/* Content */}
        <div className="p-6 space-y-3 overflow-y-auto flex- bg-slate-100">
          {/* Row 1: Account Number & Type */}
          <div className="grid grid-cols-4 gap-4">
            <MwInputText
              label={t("accounts.accountNumber")}
              value={formData?.accountNumber}
              onChange={(e) =>
                handleInputChange("accountNumber", e.target.value)
              }
              placeholder="110100011"
              classNameInput="text-center"
              invalid={!!errors.accountNumber}
              inputType="number"
              invalidMsg={errors.accountNumber}
              dir="rtl"
              min={0}
              disabled={isViewMode}
            />

            <div className="relative mt-3">
              <label className="text-xs !font-normal px-1 !text-[#261759] pb-1 block">
                {t("accounts.accountType")}
              </label>
              <div className="flex gap-6 py-2 px-3   rounded-md">
                <label className="flex items-center gap-2 text-xs cursor-pointer  text-gray-500 font-semibold">
                  <input
                    type="radio"
                    name="accountType"
                    value="main"
                    disabled
                    checked={formData.accountType === "main"}
                    onChange={(e) =>
                      handleInputChange("accountType", e.target.value)
                    }
                    className="cursor-pointer"
                  />
                  {t("accounts.primary")}
                </label>
                <label className="flex items-center gap-2 text-xs cursor-pointer text-gray-500 font-semibold">
                  <input
                    type="radio"
                    name="accountType"
                    value="key"
                    disabled
                    checked={formData.accountType === "key"}
                    onChange={(e) =>
                      handleInputChange("accountType", e.target.value)
                    }
                    className="cursor-pointer"
                  />
                  {t("accounts.sub")}
                </label>
              </div>
            </div>
            <div className="relative mt-3">
              <label className="text-xs !font-normal px-1 !text-[#261759] pb-1 block">
                {t("accounts.accountNature")}
              </label>
              <div className="flex gap-6 py-2 px-3 ">
                <label className="flex items-center gap-2 text-xs cursor-pointer text-gray-500 font-semibold">
                  <input
                    type="radio"
                    name="accountNature"
                    value="Debit"
                    disabled={isViewMode}
                    checked={formData.accountNature === "Debit"}
                    onChange={(e) =>
                      handleInputChange("accountNature", e.target.value)
                    }
                    className="cursor-pointer"
                  />
                  {t("accounts.debit")}
                </label>
                <label className="flex items-center gap-2 text-xs cursor-pointer text-gray-500 font-semibold    ">
                  <input
                    type="radio"
                    name="accountNature"
                    value="Cred"
                    disabled={isViewMode}
                    checked={formData.accountNature === "Cred"}
                    onChange={(e) =>
                      handleInputChange("accountNature", e.target.value)
                    }
                    className="cursor-pointer"
                  />
                  {t("accounts.credit")}
                </label>
              </div>
            </div>
            <div className="relative mt-3">
              <label className="text-xs !font-normal px-1 !text-[#261759] pb-1 block">
                {t("accounts.affectType")}
              </label>
              <div className="flex gap-6 py-2 px-3">
                <label className="flex items-center gap-2 text-xs cursor-pointer text-gray-500 font-semibold">
                  <input
                    type="radio"
                    name="affectType"
                    value="budget"
                    disabled={isViewMode}
                    checked={formData.affectType === "budget"}
                    onChange={(e) =>
                      handleInputChange("affectType", e.target.value)
                    }
                    className="cursor-pointer"
                  />
                  {t("accounts.balance")}
                </label>
                <label className="flex items-center gap-2 text-xs cursor-pointer  text-gray-500 font-semibold">
                  <input
                    type="radio"
                    name="affectType"
                    value="income"
                    disabled={isViewMode}
                    checked={formData.affectType === "income"}
                    onChange={(e) =>
                      handleInputChange("affectType", e.target.value)
                    }
                    className="cursor-pointer"
                  />
                  {t("accounts.income")}
                </label>
              </div>
            </div>
          </div>

          {/* Row 2: Account Names */}
          <div className="grid grid-cols-3 gap-4">
            <MwInputText
              id="accountNameAR"
              label={t("accounts.accountNameAr")}
              value={formData.accountNameAR}
              onChange={(e) =>
                handleInputChange("accountNameAR", e.target.value)
              }
              placeholder={t("accounts.accountNameAr")}
              invalid={!!errors.accountNameAR}
              invalidMsg={errors.accountNameAR}
              inputType="text"
              dir="rtl"
              disabled={isViewMode}
            />

            <MwInputText
              id="accountNameEN"
              label={t("accounts.accountNameEn")}
              value={formData.accountNameEN}
              onChange={(e) =>
                handleInputChange("accountNameEN", e.target.value)
              }
              placeholder={t("accounts.accountNameEn")}
              invalid={!!errors.accountNameEN}
              invalidMsg={errors.accountNameEN}
              inputType="text"
              dir="ltr"
              disabled={isViewMode}
            />
            <MwSelector
              label={t("setting.mainCurrency")}
              dataType="labelValue"
              _data={accountCurrency}
              selectedItem={accountCurrency.find(
                (c) => c.id === formData.accountCurrency,
              )}
              setSelectedItem={(item) =>
                handleSelectorChange("accountCurrency", item?.id)
              }
              withAddNew={false}
              withSearch={false}
              invalid={!!errors.accountCurrency}
              invalidMsg={
                errors.accountCurrency
                  ? `${t("setting.mainCurrency")} ${t("common.required")}`
                  : ""
              }
              disabled={isViewMode}
            />
          </div>

          {/* Row 4: Balances */}
          <div className="grid grid-cols-4 gap-4">
            <MwInputText
              id="openingBalance"
              label={t("accounts.openingBalance")}
              inputType="number"
              value={formData.openingBalance}
              onChange={(e) =>
                handleInputChange("openingBalance", e.target.value)
              }
              placeholder="0"
              min={0}
              disabled={isViewMode}
            />

            <MwInputText
              id="closingBalance"
              label={t("accounts.closingBalance")}
              inputType="number"
              value={formData.closingBalance}
              onChange={(e) =>
                handleInputChange("closingBalance", e.target.value)
              }
              placeholder="0"
              min={0}
              invalid={!!errors.closingBalance}
              invalidMsg={errors.closingBalance}
              disabled={isViewMode}
            />
            <div className="mt-1 col-span-2 space-y-1">
              <SelectorTree
                value={formData.costCenterAccount || ""}
                label={formData.costCenterLabel || ""}
                headerTitle={t("accounts.costCenterAccount")}
                data={costCenters}
                isAccountCenter={true}
                title={t("accounts.accountsGuide")}
                placeholderNumber={t("JournalEntry.accountNumber")}
                placeholderName={t("JournalEntry.accountName")}
                allowManualNumberInput={!isViewMode}
                withInlineSuggestions={true}
                allowedAccountTypes={[selectedCostCenterType]}
                disabled={isViewMode}
                onManualNumberChange={handleManualCostCenterNumberInput}
                onSelect={(selected) => {
                  const accountNumber =
                    selected?.accountNumber || selected?.account_no || "";

                  const accountName =
                    selected?.accountName || selected?.name || "";

                  const selectedCenter = flatCostCenters.find(
                    (c) => String(c.accountNumber) === String(accountNumber),
                  );

                  if (
                    !selectedCenter ||
                    selectedCenter.accountType !== "secondary"
                  ) {
                    setErrors((prev) => ({
                      ...prev,
                      costCenterAccount: "يجب اختيار حساب فرعي",
                    }));

                    dispatchForm({
                      type: "SET_FIELD",
                      field: "costCenterAccount",
                      value: "",
                    });

                    dispatchForm({
                      type: "SET_FIELD",
                      field: "costCenterLabel",
                      value: "",
                    });

                    return;
                  }

                  setErrors((prev) => ({
                    ...prev,
                    costCenterAccount: "",
                  }));

                  dispatchForm({
                    type: "SET_FIELD",
                    field: "costCenterAccount",
                    value: accountNumber,
                  });

                  dispatchForm({
                    type: "SET_FIELD",
                    field: "costCenterLabel",
                    value: accountName,
                  });
                }}
                invalid={!!errors.costCenterAccount}
                invalidMsg={errors.costCenterAccount}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-2 bg-slate-200 p-4 rounded-b border-t border-slate-400 justify-end sticky bottom-0">
          <MwButton type="secondary" onClick={handleClose}>
            {t("common.cancel")}
          </MwButton>
          {!isViewMode ? (
            <MwButton type="primary" onClick={handleSave}>
              {t("common.save")}
            </MwButton>
          ) : null}
        </div>
      </div>
    </MwModal>
  );
};
